/**
 * Per-game round rotation persisted in localStorage.
 *
 * Each game ships a deck of N rounds; this helper picks the next round to mount
 * and advances the cursor (modulo N) once the round is completed. Cursors are
 * keyed per `getActiveStoragePrefix() + "rotation:" + gameType` (M.2:
 * `notanyai:<characterId>:<subjectId>:rotation:…`).
 *
 * Call `setActiveStoragePrefix` in `main` before any rotation API. Safe without `localStorage`.
 */

import { getActiveStoragePrefix } from "../subjectConfig.js";

function getStorage(): Storage | null {
  if (typeof globalThis === "object" && "localStorage" in globalThis) {
    return (globalThis as unknown as { localStorage: Storage }).localStorage;
  }
  return null;
}

function rotationPrefix(): string {
  return `${getActiveStoragePrefix()}rotation:`;
}

function readRaw(gameType: string): number {
  const s = getStorage()?.getItem(rotationPrefix() + gameType);
  if (!s) return 0;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Returns the round index to render for `gameType` (0-based, modulo deck length). */
export function getRoundIndex(gameType: string, deckLength: number): number {
  if (deckLength <= 0) return 0;
  return readRaw(gameType) % deckLength;
}

/** Advances the cursor for `gameType` to the next round (modulo deck length). */
export function advanceRound(gameType: string, deckLength: number): void {
  if (deckLength <= 0) return;
  const next = (readRaw(gameType) + 1) % deckLength;
  getStorage()?.setItem(rotationPrefix() + gameType, String(next));
}

/** Resets the rotation cursor for `gameType` to 0. Mainly used by `resetAll()`. */
export function clearRotation(gameType: string): void {
  getStorage()?.removeItem(rotationPrefix() + gameType);
}
